// app/javascript/src/shared/new_components/Dropdown.jsx

import React, {useState, useRef, useEffect} from 'react'
import PropTypes from 'prop-types'
import {FaIcon} from '../components'

const GGDropdown = ({
  name,
  label,
  placeholder,
  options,
  value,
  onChange,
  state,
  message,
  hint,
  disabled,
  className,
  ...htmlOptions
}) => {
  const [ open, setOpen ] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const classes = [
    'gg-dropdown',
    state ? `gg-dropdown--${state}` : '',
    disabled ? 'gg-dropdown--disabled' : '',
    open ? 'gg-dropdown--open' : '',
    className,
  ].filter(Boolean).join(' ')

  const selectedOption = options?.find(o => o.value === value)
  const displayMsg = message || hint

  const handleSelect = (optValue) => {
    if (onChange) {
      onChange(optValue)
    }
    setOpen(false)
  }

  const handleKeyDown = (e) => {
    if (disabled) {
      return
    }
    if ([ 'Enter', ' ', 'ArrowDown' ].includes(e.key)) {
      e.preventDefault()
      if (!open) {
        setOpen(true)
      }
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
    }
  }

  return (
    <div className={classes} ref={ref} {...htmlOptions}>
      {label && (
        <label className="gg-dropdown__label" htmlFor={name}>{label}</label>
      )}

      <div className="gg-dropdown__trigger-wrap">
        <input type="hidden" name={name} value={value || ''}/>

        <button
          className="gg-dropdown__trigger"
          id={name}
          type="button"
          onClick={() => {
            if (!disabled) {
              setOpen(!open)
            }
          }}
          onKeyDown={handleKeyDown}
          disabled={disabled}
        >
          <span className={[ 'gg-dropdown__value', !selectedOption && 'gg-dropdown__value--placeholder' ].filter(Boolean).join(' ')}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <span className="gg-dropdown__chevron">
            <FaIcon icon="chevron-down"/>
          </span>
        </button>

        {open && options && (
          <div className="gg-dropdown__panel">
            {options.map((opt, index) => (
              opt.separator ? (
                <div key={`separator-${index}`} className="gg-dropdown__separator"/>
              ) : (
                <div
                  key={opt.value}
                  className={[
                    'gg-dropdown__option',
                    value === opt.value ? 'gg-dropdown__option--selected' : '',
                    opt.disabled ? 'gg-dropdown__option--disabled' : '',
                    opt.danger ? 'gg-dropdown__option--danger' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => {
                    if (!opt.disabled) {
                      handleSelect(opt.value)
                    }
                  }}
                >
                  {opt.icon && (
                    <span className="gg-dropdown__option-icon">
                      <FaIcon icon={opt.icon} style={{width: '14px', height: '14px'}}/>
                    </span>
                  )}
                  {opt.label}
                </div>
              )
            ))}
          </div>
        )}
      </div>

      {displayMsg && <span className="gg-dropdown__message">{displayMsg}</span>}
    </div>
  )
}

export default GGDropdown

GGDropdown.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
      disabled: PropTypes.bool,
      danger: PropTypes.bool,
      icon: PropTypes.string,
      separator: PropTypes.bool,
    }),
  ).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func,
  state: PropTypes.oneOf([ 'error', 'success' ]),
  message: PropTypes.string,
  hint: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
}
